import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Save, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  { key: 'mon', label: 'Monday', short: 'MON' },
  { key: 'tue', label: 'Tuesday', short: 'TUE' },
  { key: 'wed', label: 'Wednesday', short: 'WED' },
  { key: 'thu', label: 'Thursday', short: 'THU' },
  { key: 'fri', label: 'Friday', short: 'FRI' },
  { key: 'sat', label: 'Saturday', short: 'SAT' },
];
const PERIODS = [1, 2, 3, 4, 5, 6, 7, 8];

// Curriculum subjects + commonly-scheduled school activities (incl. Sports & breaks).
// First entry "" lets HT clear a cell back to empty.
const SUBJECT_OPTIONS = [
  '',
  'Mathematics',
  'Science',
  'English',
  'Social Studies',
  'Hindi',
  'Telugu',
  'Sanskrit',
  'Sports',
  'Computer',
  'Art & Craft',
  'Music',
  'Library',
  'Moral Science',
  'General Knowledge',
  'Assembly',
  'Lunch Break',
  'Short Break',
];

// Colour palette for each subject — each tile gets a distinct, calm colour pair
// (background + text) so the whole grid reads at-a-glance.
const SUBJECT_COLORS = {
  'Mathematics':       { bg: 'bg-blue-100',    border: 'border-blue-300',    text: 'text-blue-800',    dot: 'bg-blue-500' },
  'Science':           { bg: 'bg-green-100',   border: 'border-green-300',   text: 'text-green-800',   dot: 'bg-green-500' },
  'English':           { bg: 'bg-purple-100',  border: 'border-purple-300',  text: 'text-purple-800',  dot: 'bg-purple-500' },
  'Social Studies':    { bg: 'bg-orange-100',  border: 'border-orange-300',  text: 'text-orange-800',  dot: 'bg-orange-500' },
  'Hindi':             { bg: 'bg-pink-100',    border: 'border-pink-300',    text: 'text-pink-800',    dot: 'bg-pink-500' },
  'Telugu':            { bg: 'bg-fuchsia-100', border: 'border-fuchsia-300', text: 'text-fuchsia-800', dot: 'bg-fuchsia-500' },
  'Sanskrit':          { bg: 'bg-rose-100',    border: 'border-rose-300',    text: 'text-rose-800',    dot: 'bg-rose-500' },
  'Sports':            { bg: 'bg-lime-100',    border: 'border-lime-300',    text: 'text-lime-800',    dot: 'bg-lime-600' },
  'Computer':          { bg: 'bg-cyan-100',    border: 'border-cyan-300',    text: 'text-cyan-800',    dot: 'bg-cyan-500' },
  'Art & Craft':       { bg: 'bg-amber-100',   border: 'border-amber-300',   text: 'text-amber-800',   dot: 'bg-amber-500' },
  'Music':             { bg: 'bg-violet-100',  border: 'border-violet-300',  text: 'text-violet-800',  dot: 'bg-violet-500' },
  'Library':           { bg: 'bg-teal-100',    border: 'border-teal-300',    text: 'text-teal-800',    dot: 'bg-teal-500' },
  'Moral Science':     { bg: 'bg-indigo-100',  border: 'border-indigo-300',  text: 'text-indigo-800',  dot: 'bg-indigo-500' },
  'General Knowledge': { bg: 'bg-emerald-100', border: 'border-emerald-300', text: 'text-emerald-800', dot: 'bg-emerald-500' },
  'Assembly':          { bg: 'bg-yellow-100',  border: 'border-yellow-300',  text: 'text-yellow-800',  dot: 'bg-yellow-500' },
  'Lunch Break':       { bg: 'bg-slate-100',   border: 'border-slate-300',   text: 'text-slate-700',   dot: 'bg-slate-500' },
  'Short Break':       { bg: 'bg-stone-100',   border: 'border-stone-300',   text: 'text-stone-700',   dot: 'bg-stone-500' },
};

const colorFor = (subject) => SUBJECT_COLORS[subject] || { bg: 'bg-white', border: 'border-gray-200', text: 'text-gray-400', dot: 'bg-gray-300' };

/**
 * Class Timetable — editable for HT (`editable` prop), read-only for others.
 * Colour-coded subject blocks + bold period numbers + clear day rows.
 */
const Timetable = ({ classLevel = '6', section = 'A', editable = false }) => {
  const [cells, setCells] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teachers, setTeachers] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const reqs = [axios.get(`${API}/timetable/${classLevel}`, { params: { section } })];
      if (editable) {
        reqs.push(
          axios.get(`${API}/head-teacher/teachers`).catch(() => ({ data: [] }))
        );
      }
      const [tRes, teachersRes] = await Promise.all(reqs);
      const map = {};
      (tRes.data?.cells || []).forEach(c => { map[`${c.day}-${c.period}`] = c; });
      setCells(map);
      if (editable) {
        setTeachers(teachersRes?.data || []);
      }
    } catch (e) {
      // silent
    } finally { setLoading(false); }
  }, [classLevel, section, editable]);

  useEffect(() => { load(); }, [load]);

  const updateCell = (day, period, field, value) => {
    setCells(prev => ({
      ...prev,
      [`${day}-${period}`]: { ...prev[`${day}-${period}`], day, period, [field]: value }
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const allCells = Object.values(cells).filter(c => c.subject || c.teacher_name);
      await axios.put(`${API}/head-teacher/timetable`, {
        class_level: classLevel,
        section,
        cells: allCells,
      });
      toast.success('Timetable saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  // Subject colour legend — built dynamically from what's actually on the grid.
  const subjectsOnGrid = Array.from(new Set(Object.values(cells).map(c => c.subject).filter(Boolean)));

  if (loading) return <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin text-teal-500" /></div>;

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100" data-testid={`timetable-${classLevel}`}>
      {/* Header */}
      <div className="px-5 py-4 bg-gradient-to-r from-teal-600 to-emerald-600 text-white flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Calendar size={20} /> Class {classLevel}{section ? `-${section}` : ''} Timetable
          </h3>
          <p className="text-xs text-teal-100 mt-0.5">{editable ? 'Click a cell to assign subject & teacher' : 'Weekly schedule'}</p>
        </div>
        {editable && (
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            className="bg-white text-teal-700 hover:bg-teal-50"
            data-testid="save-timetable-btn"
          >
            {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />} Save Timetable
          </Button>
        )}
      </div>

      {editable && teachers.length === 0 && (
        <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-700">
          No teachers added yet. Add teachers under <strong>Teachers</strong> first — they will then appear in the teacher dropdown below.
        </div>
      )}

      {/* Subject Legend (read-only only — keeps the grid focused) */}
      {!editable && subjectsOnGrid.length > 0 && (
        <div className="px-5 py-3 bg-gray-50 border-b flex flex-wrap gap-x-4 gap-y-2">
          {subjectsOnGrid.map(sub => {
            const c = colorFor(sub);
            return (
              <div key={sub} className="flex items-center gap-1.5 text-xs text-gray-700">
                <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />
                {sub}
              </div>
            );
          })}
        </div>
      )}

      {/* Grid */}
      <div className="overflow-x-auto p-3 md:p-4">
        <table className="w-full border-separate" style={{ borderSpacing: '6px' }}>
          <thead>
            <tr>
              <th className="text-center text-[11px] uppercase tracking-wide text-gray-500 font-semibold py-2 w-20">Period</th>
              {DAYS.map(d => (
                <th key={d.key} className="text-center text-[11px] uppercase tracking-wide text-gray-500 font-semibold py-2 min-w-[140px]">
                  <div className="text-gray-800 font-bold text-sm">{d.short}</div>
                  <div className="text-[10px] text-gray-400 font-normal">{d.label}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERIODS.map(p => (
              <tr key={p}>
                {/* Period number — large, friendly bubble */}
                <td className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 text-white font-bold text-xl shadow-sm" data-testid={`period-${p}`}>
                    {p}
                  </div>
                </td>
                {DAYS.map(d => {
                  const c = cells[`${d.key}-${p}`] || {};
                  const col = colorFor(c.subject);
                  if (editable) {
                    return (
                      <td key={d.key} className={`align-top rounded-xl border-2 ${col.border} ${col.bg} p-2 transition-all hover:shadow-md`}>
                        <select
                          value={c.subject || ''}
                          onChange={e => updateCell(d.key, p, 'subject', e.target.value)}
                          className={`w-full border-0 bg-transparent px-1 py-0.5 text-xs font-semibold ${col.text} focus:outline-none focus:ring-1 focus:ring-teal-400 rounded`}
                          data-testid={`tt-subject-${d.key}-${p}`}
                        >
                          {SUBJECT_OPTIONS.map(s => (
                            <option key={s || 'empty'} value={s}>{s || '— Subject —'}</option>
                          ))}
                        </select>
                        <select
                          value={c.teacher_name || ''}
                          onChange={e => updateCell(d.key, p, 'teacher_name', e.target.value)}
                          className="w-full mt-1 border-0 bg-white/70 px-1 py-0.5 text-[11px] text-gray-700 focus:outline-none focus:ring-1 focus:ring-teal-400 rounded"
                          data-testid={`tt-teacher-${d.key}-${p}`}
                        >
                          <option value="">— Teacher —</option>
                          {teachers.map(t => (
                            <option key={t.id} value={t.name}>{t.name}</option>
                          ))}
                        </select>
                      </td>
                    );
                  }
                  // Read-only
                  return (
                    <td key={d.key} className={`rounded-xl border-2 ${col.border} ${col.bg} p-3 min-h-[64px] align-top`}>
                      {c.subject ? (
                        <div>
                          <p className={`text-sm font-bold ${col.text} truncate`}>{c.subject}</p>
                          {c.teacher_name && <p className="text-[11px] text-gray-600 mt-0.5 truncate">{c.teacher_name}</p>}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-300 text-center">—</p>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Timetable;
